<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

export default {
  components: {
    NextInput,
    NextButton,
    WootMessageEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    mediaAttachment: { type: Object, default: null },
    mediaAttachments: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      attachments: [null],
      removeAttachmentIds: [],
      removeAttachment: false,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    pageTitle() {
      return this.$t('CANNED_MGMT.EDIT.TITLE');
    },
    existingAttachments() {
      if (this.mediaAttachments.length) {
        return this.mediaAttachments;
      }

      return this.mediaAttachment ? [this.mediaAttachment] : [];
    },
    visibleExistingAttachments() {
      return this.existingAttachments.filter(
        attachment => !this.removeAttachmentIds.includes(attachment.signed_id)
      );
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.attachments = [null];
      this.removeAttachmentIds = [];
      this.removeAttachment = false;
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    onAttachmentChange(index, event) {
      const [file] = event.target.files;
      this.attachments.splice(index, 1, file || null);
      this.removeAttachment = false;
    },
    addAttachmentRow() {
      this.attachments.push(null);
    },
    removeAttachmentRow(index) {
      this.attachments.splice(index, 1);
      if (!this.attachments.length) {
        this.addAttachmentRow();
      }
    },
    clearAttachment(attachment) {
      this.removeAttachmentIds.push(attachment.signed_id);
    },
    editCannedResponse() {
      // Show loading on button
      this.editCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          attachments: this.attachments.filter(Boolean),
          remove_attachment: this.removeAttachment,
          remove_attachment_ids: this.removeAttachmentIds,
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <div class="flex max-h-[85vh] flex-col overflow-auto">
    <woot-modal-header compact :header-title="pageTitle" />
    <form
      class="modal-content flex w-full flex-col gap-3 !px-5 !pb-5 !pt-2"
      @submit.prevent="editCannedResponse()"
    >
      <div class="flex flex-col gap-3 border-b border-n-weak pb-3">
        <div class="flex flex-col gap-1">
          <label
            class="mb-0 text-xs font-medium text-n-slate-12"
            :class="{ 'text-n-ruby-9': v$.shortCode.$error }"
          >
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
          </label>
          <NextInput
            v-model="shortCode"
            size="sm"
            type="text"
            :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
            @input="v$.shortCode.$touch"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label
            class="mb-0 text-xs font-medium text-n-slate-12"
            :class="{ 'text-n-ruby-9': v$.content.$error }"
          >
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div
            class="[&_.ProseMirror-menubar]:hidden [&_.ProseMirror-woot-style]:min-h-[8rem] [&_.ProseMirror-woot-style_p]:text-sm"
          >
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{
                'outline outline-1 outline-n-ruby-9 rounded-xl':
                  v$.content.$error,
              }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="mb-0 text-xs font-medium text-n-slate-12">
          {{ $t('CANNED_MGMT.EDIT.FORM.ATTACHMENT.LABEL') }}
        </label>
        <div
          v-for="attachment in visibleExistingAttachments"
          :key="attachment.signed_id"
          class="flex items-center justify-between gap-2 rounded-lg bg-n-slate-3 px-2.5 py-1.5"
        >
          <span class="min-w-0 truncate text-xs text-n-slate-12">
            {{ attachment.filename }}
          </span>
          <NextButton
            slate
            ghost
            size="sm"
            type="button"
            icon="i-lucide-trash"
            @click.prevent="clearAttachment(attachment)"
          />
        </div>
        <div
          class="flex flex-col gap-2 rounded-lg border border-dashed border-n-weak bg-n-alpha-black2 px-2.5 py-3"
        >
          <div
            v-for="(_attachment, index) in attachments"
            :key="index"
            class="flex flex-wrap items-center gap-2"
          >
            <span class="w-10 shrink-0 text-xs font-medium text-n-slate-11">
              {{ $t('CANNED_MGMT.EDIT.FORM.ATTACHMENT.FILE_LABEL') }}
            </span>
            <input
              type="file"
              class="min-w-0 flex-1 cursor-pointer rounded-md border-0 bg-n-solid-1 px-2 py-1.5 text-xs text-n-slate-12 outline outline-1 outline-n-weak file:mr-2 file:rounded-md file:border-0 file:bg-n-slate-3 file:px-2 file:py-1 file:text-xs file:font-medium file:text-n-slate-12"
              :aria-label="$t('CANNED_MGMT.EDIT.FORM.ATTACHMENT.FILE_LABEL')"
              @change="onAttachmentChange(index, $event)"
            />
            <NextButton
              v-if="attachments.length > 1"
              type="button"
              slate
              ghost
              size="sm"
              icon="i-lucide-trash"
              @click.prevent="removeAttachmentRow(index)"
            />
          </div>
          <NextButton
            type="button"
            faded
            slate
            size="sm"
            class="w-full border border-dashed border-n-weak bg-transparent"
            icon="i-lucide-plus"
            :label="$t('CANNED_MGMT.EDIT.FORM.ATTACHMENT.ADD_FILE')"
            @click.prevent="addAttachmentRow"
          />
        </div>
        <p class="mb-0 text-xs leading-snug text-n-slate-11">
          {{ $t('CANNED_MGMT.EDIT.FORM.ATTACHMENT.HELP_TEXT') }}
        </p>
      </div>

      <div class="flex flex-row justify-end gap-2 border-t border-n-weak pt-3">
        <NextButton
          faded
          slate
          size="sm"
          type="button"
          :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          blue
          solid
          size="sm"
          :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
          :disabled="
            v$.content.$invalid ||
            v$.shortCode.$invalid ||
            editCanned.showLoading
          "
          :is-loading="editCanned.showLoading"
        />
      </div>
    </form>
  </div>
</template>
